import {
  booleanPointInPolygon,
  circle as createTurfCircle,
  polygon as createTurfPolygon,
  destination,
  difference,
  distance,
  featureCollection,
  lineString as createTurfLineString,
  point,
  pointToLineDistance,
  union,
} from '@turf/turf';
import { getIpawsPolygonFindings } from './ipawsPolygon.utils';

// Turf is the map-math library used below for real distances and whole-area boundary checks.

/**
 * A point on the map. Leaflet uses the names `lat` and `lng`, so the editor keeps that shape.
 * @typedef {{ lat: number, lng: number }} IpawsMapCoordinate
 */

/** Names used in saved drafts to tell the three editable shape types apart. */
export const IPAWS_AREA_TYPES = {
  POLYGON: 'polygon',
  CIRCLE: 'circle',
  SQUARE: 'square',
};

/** Limits shared by the map editor and the final alert checks. */
export const IPAWS_AREA_LIMITS = {
  maximumShapeCount: 10,
  maximumPointCount: 100,
};
export const NWEM_MAXIMUM_POLYGON_POINT_COUNT = 20;
export const NWEM_MAXIMUM_DRAWN_POLYGON_VERTEX_COUNT = NWEM_MAXIMUM_POLYGON_POINT_COUNT - 1;

// New circles and squares start at useful sizes, while typed or dragged sizes cannot reach zero.
export const DEFAULT_IPAWS_CIRCLE_RADIUS_KM = 1;
export const DEFAULT_IPAWS_SQUARE_SIDE_LENGTH_KM = 2;
export const MINIMUM_IPAWS_SHAPE_SIZE_KM = 0.01;

/**
 * The SAME geocode for a jurisdiction, or an empty string when it has none.
 * A county GEOID is the 5-digit state+county FIPS; its SAME code is that with a leading 0.
 * A place GEOID is 7 digits and has no SAME code: IPAWS targets counties.
 */
export const getIpawsSameGeocode = (jurisdiction) => {
  const geoid = String(jurisdiction?.GEOID ?? '');
  return /^\d{5}$/.test(geoid) ? `0${geoid}` : '';
};

/** True when the draft names a SAME geocode. The whole county is then the target. */
export const hasIpawsSameGeocode = (draft) =>
  !Array.isArray(draft) &&
  Boolean(draft?.geocodes?.some((code) => code?.valueName === 'SAME' && code?.value));

/** Legacy EAS and NWR processing accepts at most 31 SAME geocodes per alert. */
export const IPAWS_LEGACY_SAME_GEOCODE_LIMIT = 31;

/** Counts complete SAME geocode elements in a draft or geocode list. */
export const getIpawsSameGeocodeCount = (draftOrGeocodes = {}) => {
  const geocodes = Array.isArray(draftOrGeocodes) ? draftOrGeocodes : draftOrGeocodes?.geocodes;

  return Array.isArray(geocodes)
    ? geocodes.filter((code) => code?.valueName === 'SAME' && code?.value).length
    : 0;
};

const isLngLatPosition = (value) =>
  Array.isArray(value) &&
  value.length >= 2 &&
  Number.isFinite(value[0]) &&
  Number.isFinite(value[1]);

/**
 * Every outer ring of a jurisdiction boundary as `{ lat, lng }` points, for drawing only.
 * Reads `boundary_coordinates` (the Census ring as stored) before `original_coordinates`, which
 * the jurisdictions list serves buffered by 1000 ft. Accepts a GeoJSON Polygon, a MultiPolygon,
 * and a bare ring; holes are not drawn. Nothing here is ever sent to IPAWS: a Census ring has
 * hundreds of points, far over the CAP limit, and a buffered ring is not the jurisdiction.
 */
export const getIpawsJurisdictionBoundaryRings = (jurisdiction) => {
  const raw = jurisdiction?.boundary_coordinates ?? jurisdiction?.original_coordinates;
  let parsed = raw;
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return [];
    }
  }

  const rings = [];
  const collect = (node, depth) => {
    if (!Array.isArray(node) || depth > 4) return;
    if (isLngLatPosition(node[0])) {
      const ring = node.filter(isLngLatPosition).map(([lng, lat]) => ({ lat, lng }));
      if (ring.length >= 3) rings.push(ring);
      return;
    }
    // A Polygon lists its outer ring first and holes after it; only the outer ring is drawn.
    if (Array.isArray(node[0]) && isLngLatPosition(node[0][0])) {
      collect(node[0], depth + 1);
      return;
    }
    node.forEach((child) => collect(child, depth + 1));
  };
  collect(parsed, 0);
  return rings;
};

/**
 * What the draft keeps about a chosen jurisdiction: its identity and its outline. The coordinate
 * strings themselves stay out of the draft.
 */
export const describeResolvedJurisdiction = (jurisdiction) => {
  if (!jurisdiction) return null;
  const GEOID = String(jurisdiction.GEOID ?? '');
  return {
    GEOID,
    name: jurisdiction.name ?? jurisdiction.NAME ?? '',
    place_id: jurisdiction.place_id ?? `jurisdiction-${GEOID}`,
    lookup_type: jurisdiction.lookup_type ?? 'county',
    boundaryRings: getIpawsJurisdictionBoundaryRings(jurisdiction),
  };
};

let fallbackAreaIdSequence = 0;

/**
 * Creates the unique ID used to select, edit, and delete one shape.
 * Modern browsers create the ID for us. The fallback keeps this working in older environments.
 * @returns {string} A new shape ID.
 */
export const createIpawsAreaShapeId = () => {
  const generatedId = globalThis.crypto?.randomUUID?.();
  if (generatedId) return `ipaws-area-${generatedId}`;

  fallbackAreaIdSequence += 1;
  return `ipaws-area-${Date.now()}-${fallbackAreaIdSequence}`;
};

const isValidWgs84Coordinate = (coordinate) =>
  Number.isFinite(coordinate?.lat) &&
  Number.isFinite(coordinate?.lng) &&
  coordinate.lat >= -90 &&
  coordinate.lat <= 90 &&
  coordinate.lng >= -180 &&
  coordinate.lng <= 180;

const areCoordinatesEqual = (firstCoordinate, secondCoordinate) =>
  Boolean(
    firstCoordinate &&
    secondCoordinate &&
    firstCoordinate.lat === secondCoordinate.lat &&
    firstCoordinate.lng === secondCoordinate.lng,
  );

/**
 * Removes the repeated last point from a closed polygon.
 * The editor keeps only the points a user can move. The repeated closing point is added later
 * when the alert data is built.
 * @param {IpawsMapCoordinate[]} coordinates Points that may include a repeated last point.
 * @returns {IpawsMapCoordinate[]} Points without the repeated closing point.
 */
export const getIpawsOpenPolygonCoordinates = (coordinates = []) => {
  if (!Array.isArray(coordinates)) return [];

  return coordinates.length > 1 && areCoordinatesEqual(coordinates[0], coordinates.at(-1))
    ? coordinates.slice(0, -1)
    : coordinates;
};

const isPositiveDistance = (value) => Number.isFinite(Number(value)) && Number(value) > 0;

/**
 * Builds the four map corners for a square from its center and side length.
 * The Earth is curved, so this uses real map distances instead of adding directly to latitude and
 * longitude.
 * @param {IpawsMapCoordinate} center The square's center point.
 * @param {number} squareWidthInKm The full width of the square in kilometers.
 * @returns {IpawsMapCoordinate[]} Four corners in clockwise order.
 */
export const getIpawsSquareCoordinates = (center, squareWidthInKm) => {
  if (!isValidWgs84Coordinate(center) || !isPositiveDistance(squareWidthInKm)) return [];

  const centerPoint = point([center.lng, center.lat]);
  // The distance from a square's center to a corner is its side divided by the square root of 2.
  const distanceToCornerKm = Number(squareWidthInKm) / Math.sqrt(2);

  // These directions create northeast, southeast, southwest, and northwest corners.
  // 45°  = northeast
  // 135° = southeast
  // 225° = southwest
  // 315° = northwest
  return [45, 135, 225, 315].map((direction) => {
    const [longitude, latitude] = destination(centerPoint, distanceToCornerKm, direction, {
      units: 'kilometers',
    }).geometry.coordinates; // Determines the coordinates if heading from the center in the given direction by the given distance.

    return { lat: latitude, lng: longitude };
  });
};

/**
 * Returns the points where resize handles should appear.
 * Squares have one handle on each corner. Circles have one handle on their right edge because the
 * saved circle only needs a center and radius.
 * @param {object} shape The selected square or circle.
 * @returns {IpawsMapCoordinate[]} The points where resize handles should be drawn.
 */
export const getIpawsAreaResizeHandleCoordinates = (shape) => {
  if (shape?.type === IPAWS_AREA_TYPES.SQUARE) {
    return getIpawsSquareCoordinates(shape.center, shape.sideLengthKm);
  }

  if (
    shape?.type !== IPAWS_AREA_TYPES.CIRCLE ||
    !isValidWgs84Coordinate(shape.center) ||
    !isPositiveDistance(shape.radiusKm)
  ) {
    return [];
  }

  const [longitude, latitude] = destination(
    point([shape.center.lng, shape.center.lat]), // center of circle
    Number(shape.radiusKm), // radius in KM
    90, // Heading in degrees (0 = north, 90 = east, 180 = south, 270 = west)
    { units: 'kilometers' },
  ).geometry.coordinates;

  return [{ lat: latitude, lng: longitude }]; // Right edge of the circle for the resize handle.
};

/**
 * When a shape is resized using the handles it calculates the new size based on the handle's position.
 * @param {object} shape The square or circle being resized.
 * @param {IpawsMapCoordinate} handlePosition The handle's new map position.
 * @returns {{ radiusKm: number } | { sideLengthKm: number } | null} The size change to save.
 */
export const getIpawsAreaResizeUpdates = (shape, handlePosition) => {
  if (
    !isValidWgs84Coordinate(shape?.center) ||
    !isValidWgs84Coordinate(handlePosition) ||
    ![IPAWS_AREA_TYPES.CIRCLE, IPAWS_AREA_TYPES.SQUARE].includes(shape?.type)
  ) {
    return null;
  }

  const centerToHandleKm = distance(
    point([shape.center.lng, shape.center.lat]),
    point([handlePosition.lng, handlePosition.lat]),
    { units: 'kilometers' },
  );
  // For a circle, center-to-handle is the radius. For a square, it is center-to-corner, so turn
  // that diagonal distance back into the full side length.
  const shapeSizeKm =
    shape.type === IPAWS_AREA_TYPES.SQUARE ? centerToHandleKm * Math.sqrt(2) : centerToHandleKm;
  // Rounding avoids saving long decimal values after every drag. The minimum prevents the shape from becoming overly small
  const constrainedSizeKm = Math.max(MINIMUM_IPAWS_SHAPE_SIZE_KM, Number(shapeSizeKm.toFixed(3)));

  return shape.type === IPAWS_AREA_TYPES.CIRCLE
    ? { radiusKm: constrainedSizeKm }
    : { sideLengthKm: constrainedSizeKm };
};

export const getIpawsPolygonCenter = (coordinates = []) => {
  const points = getIpawsOpenPolygonCoordinates(coordinates);
  if (!points.length) return null;
  return points.reduce(
    (center, coordinate) => ({
      lat: center.lat + Number(coordinate.lat) / points.length,
      lng: center.lng + Number(coordinate.lng) / points.length,
    }),
    { lat: 0, lng: 0 },
  );
};

export const moveIpawsPolygon = (coordinates = [], nextCenter) => {
  const center = getIpawsPolygonCenter(coordinates);
  if (!center || !isValidWgs84Coordinate(nextCenter)) return coordinates;
  const latitudeOffset = nextCenter.lat - center.lat;
  const longitudeOffset = nextCenter.lng - center.lng;
  return coordinates.map(({ lat, lng }) => ({
    lat: lat + latitudeOffset,
    lng: lng + longitudeOffset,
  }));
};

export const transformIpawsPolygon = (coordinates = [], handlePosition, transformType) => {
  const center = getIpawsPolygonCenter(coordinates);
  if (!center || !isValidWgs84Coordinate(handlePosition)) return coordinates;
  const reference = coordinates.reduce(
    (furthest, coordinate) => {
      const distanceSquared =
        (coordinate.lat - center.lat) ** 2 + (coordinate.lng - center.lng) ** 2;
      return distanceSquared > furthest.distanceSquared
        ? { coordinate, distanceSquared }
        : furthest;
    },
    { coordinate: coordinates[0], distanceSquared: -1 },
  ).coordinate;
  if (!reference) return coordinates;

  const referenceAngle = Math.atan2(reference.lat - center.lat, reference.lng - center.lng);
  const handleAngle = Math.atan2(handlePosition.lat - center.lat, handlePosition.lng - center.lng);
  const referenceDistance = Math.hypot(reference.lat - center.lat, reference.lng - center.lng);
  const handleDistance = Math.hypot(
    handlePosition.lat - center.lat,
    handlePosition.lng - center.lng,
  );
  const angle = transformType === 'rotate' ? handleAngle - referenceAngle : 0;
  const scale =
    transformType === 'resize' && referenceDistance ? handleDistance / referenceDistance : 1;
  const cosine = Math.cos(angle);
  const sine = Math.sin(angle);

  return coordinates.map((coordinate) => {
    const x = coordinate.lng - center.lng;
    const y = coordinate.lat - center.lat;
    return {
      lat: center.lat + (x * sine + y * cosine) * scale,
      lng: center.lng + (x * cosine - y * sine) * scale,
    };
  });
};

/**
 * Returns the editable shapes stored by the current IPAWS draft format.
 * A full draft stores them in `areaShapes`; callers that already have the list can pass it directly.
 * @param {object | object[]} draftOrShapes A current IPAWS draft or its shape list.
 * @returns {object[]} The current polygon, square, and circle shapes, or an empty list.
 */
export const getIpawsAreaShapes = (draftOrShapes = {}) => {
  const areaShapes = Array.isArray(draftOrShapes) ? draftOrShapes : draftOrShapes?.areaShapes;
  return Array.isArray(areaShapes) ? areaShapes : [];
};

/**
 * Converts stored FEMA CAP areas into shapes accepted by the IPAWS map editor.
 * @param {object[]} areas Stored CAP area objects with polygon or circle geometry.
 * @returns {import('./ipawsSubmission.utils').AlertCreatorAreaShape[]} Editable polygon and circle
 * shapes for an Update draft.
 */
export const getUpdateAreaShapes = (areas = []) =>
  areas.flatMap((area, areaIndex) => [
    ...(area.polygon ?? []).flatMap((ring, polygonIndex) => {
      const coordinates = ring
        .map(({ latitude, longitude }) => ({ lat: latitude, lng: longitude }))
        .filter(({ lat, lng }) => Number.isFinite(lat) && Number.isFinite(lng));
      if (coordinates.length > 1) {
        const first = coordinates[0];
        const last = coordinates[coordinates.length - 1];
        if (first.lat === last.lat && first.lng === last.lng) coordinates.pop();
      }
      return coordinates.length >= 3
        ? [
            {
              id: `fema-update-polygon-${areaIndex}-${polygonIndex}`,
              type: 'polygon',
              coordinates,
              lastInserted: [],
            },
          ]
        : [];
    }),
    ...(area.circle ?? []).flatMap((circle, circleIndex) => {
      const latitude = circle.center?.latitude;
      const longitude = circle.center?.longitude;
      const radiusKm = Number(circle.radius);
      return Number.isFinite(latitude) && Number.isFinite(longitude) && radiusKm > 0
        ? [
            {
              id: `fema-update-circle-${areaIndex}-${circleIndex}`,
              type: 'circle',
              center: { lat: latitude, lng: longitude },
              radiusKm,
            },
          ]
        : [];
    }),
  ]);

/**
 * Returns the custom shape name or its default label throughout the editor.
 * @param {object} shape The shape to name.
 * @param {number} index The shape's position in the list.
 * @returns {string} A name such as `Square 2`.
 */
export const getIpawsAreaShapeLabel = (shape, index) => {
  if (typeof shape?.name === 'string' && shape.name.trim()) return shape.name.trim();

  const typeLabel =
    shape?.type === IPAWS_AREA_TYPES.CIRCLE
      ? 'Circle'
      : shape?.type === IPAWS_AREA_TYPES.SQUARE
        ? 'Square'
        : 'Polygon';

  return `${typeLabel} ${index + 1}`;
};

/**
 * Returns editable polygon points for a polygon-like shape.
 * Squares are stored as center plus side length, so their four corners are built here when needed.
 * Circles return no polygon points because they stay circles in the final alert data.
 * @param {object} shape The shape to read.
 * @returns {IpawsMapCoordinate[]} Polygon points or generated square corners.
 */
export const getIpawsAreaPolygonCoordinates = (shape) => {
  if (shape?.type === IPAWS_AREA_TYPES.POLYGON) {
    return Array.isArray(shape.coordinates) ? shape.coordinates : [];
  }

  if (shape?.type === IPAWS_AREA_TYPES.SQUARE) {
    return getIpawsSquareCoordinates(shape.center, shape.sideLengthKm);
  }

  return [];
};

/**
 * Returns points around a shape's outside edge for map-only checks.
 * A circle is temporarily changed into many edge points here so the app can check the whole circle
 * against the permitted area. This does not change how the circle is saved or submitted.
 * @param {object} shape The shape whose edge is needed.
 * @returns {IpawsMapCoordinate[]} Points around the shape's outside edge.
 */
export const getIpawsAreaShapeBoundaryCoordinates = (shape) => {
  if (shape?.type !== IPAWS_AREA_TYPES.CIRCLE) {
    return getIpawsAreaPolygonCoordinates(shape);
  }

  if (!isValidWgs84Coordinate(shape.center) || !isPositiveDistance(shape.radiusKm)) {
    return [];
  }

  const circleFeature = createTurfCircle(
    [shape.center.lng, shape.center.lat],
    Number(shape.radiusKm),
    { steps: 128, units: 'kilometers' }, // Creates 128 points around the circle edge
  );

  // We check the coordinates of the points around the circles edge.
  // Turf repeats the first point at the end so we remove it here before mapping and returning an array of the coordinates for the circle edge points.
  return circleFeature.geometry.coordinates[0].slice(0, -1).map(([longitude, latitude]) => ({
    lat: latitude,
    lng: longitude,
  }));
};

/**
 * Returns Leaflet bounds covering every current IPAWS shape, including a circle's full edge.
 * @param {object | object[]} draftOrShapes A current IPAWS draft or its shape list.
 * @returns {[[number, number], [number, number]]|undefined} South-west and north-east bounds.
 */
export const getIpawsAreaBounds = (draftOrShapes = {}) => {
  const coordinates = getIpawsAreaShapes(draftOrShapes).flatMap(
    getIpawsAreaShapeBoundaryCoordinates,
  );
  if (!coordinates.length) return undefined;

  const latitudes = coordinates.map(({ lat }) => lat);
  const longitudes = coordinates.map(({ lng }) => lng);

  return [
    [Math.min(...latitudes), Math.min(...longitudes)],
    [Math.max(...latitudes), Math.max(...longitudes)],
  ];
};

/**
 * Converts a polygon's open coordinates into a closed ring suitable for Turf.js.
 *
 * Turf requires the first and last coordinates to be the same to form a closed ring.
 *
 * Such as [{ lat: 0, lng: 0 }, { lat: 1, lng: 0 }, { lat: 1, lng: 1 }, { lat: 0, lng: 0 }]
 * @param {IpawsMapCoordinate[]} coordinates Open polygon coordinates.
 * @returns {Array<Array<number>>|null} Closed polygon coordinates suitable for Turf.js or null if invalid.
 */
const createClosedTurfRing = (coordinates) => {
  const openCoordinates = getIpawsOpenPolygonCoordinates(coordinates);
  if (openCoordinates.length < 3 || !openCoordinates.every(isValidWgs84Coordinate)) return null;

  // Turf expects longitude first, while Leaflet and this editor use latitude first.
  const positions = openCoordinates.map(({ lat, lng }) => [lng, lat]);
  // A closed map ring repeats its first point at the end.
  return [...positions, positions[0]];
};

/**
 * Every polygon of a jurisdiction boundary WITH its holes, as `{ lat, lng }` rings — for the
 * containment check, which must honour an enclave (an independent city inside a county) exactly
 * as the backend does. `getIpawsJurisdictionBoundaryRings` keeps serving the outer rings for
 * drawing. Accepts a GeoJSON Polygon, a MultiPolygon, and a bare ring.
 * @returns {Array<Array<Array<{ lat: number, lng: number }>>>} polygons → rings (outer first) → points.
 */
export const getIpawsJurisdictionPolygons = (jurisdiction) => {
  const raw = jurisdiction?.boundary_coordinates ?? jurisdiction?.original_coordinates;
  let parsed = raw;
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  const toRing = (ring) => ring.filter(isLngLatPosition).map(([lng, lat]) => ({ lat, lng }));
  const polygons = [];
  const collect = (node, depth) => {
    if (!Array.isArray(node) || depth > 4) return;
    if (isLngLatPosition(node[0])) {
      const ring = toRing(node);
      if (ring.length >= 3) polygons.push([ring]);
      return;
    }
    if (Array.isArray(node[0]) && isLngLatPosition(node[0][0])) {
      const rings = node.map(toRing).filter((ring) => ring.length >= 3);
      if (rings.length) polygons.push(rings);
      return;
    }
    node.forEach((child) => collect(child, depth + 1));
  };
  collect(parsed, 0);
  return polygons;
};

/**
 * Combines the authorized area the draft targets into one Turf object for the shape check (the
 * rule since 2026-08-28: the FEMA-authorized area, never the workplace's own zones). Reads the
 * area's `polygons` (rings with holes) when it has them, else its outer `rings`. A MultiPolygon
 * area — a county with islands, a whole state — stays one object; parts that touch may merge.
 * This only prepares the permitted area; it does not check an alert shape.
 * @param {{ polygons?: object[][][], rings?: object[][] }} permittedArea From `permittedAreaForDraft`.
 * @returns {object|null} A Turf Polygon or MultiPolygon, or `null` when nothing converts safely.
 */
const combinePermittedArea = (permittedArea) => {
  try {
    const polygons =
      Array.isArray(permittedArea?.polygons) && permittedArea.polygons.length
        ? permittedArea.polygons
        : (Array.isArray(permittedArea?.rings) ? permittedArea.rings : []).map((ring) => [ring]);
    const features = polygons
      .map((rings) => {
        const [outer, ...holes] = rings.map((ring) => createClosedTurfRing(ring));
        // An outer ring that does not convert drops the polygon; a hole that does not is ignored.
        return outer ? createTurfPolygon([outer, ...holes.filter(Boolean)]) : null;
      })
      .filter(Boolean);

    if (!features.length) return null;
    if (features.length === 1) return features[0];

    // Return one Turf object; disconnected parts remain separate inside a MultiPolygon.
    return union(featureCollection(features));
  } catch {
    // Invalid or incomplete boundary data must not be treated as permission to send an alert.
    return null;
  }
};

/**
 * Checks whether an entire IPAWS shape is inside the permitted area (the targeted authorized area).
 * The backend runs the same Turf check (`FEMAGeographyCheck.ops.ts`) and records a refusal.
 * Turf's `difference` returns `null` when subtracting the permitted areas leaves no part of the
 * shape behind.
 * @param {object} shape The polygon, square, or circle being checked.
 * @param {object|null} permittedAreas A Turf Polygon or MultiPolygon containing the permitted areas.
 * @returns {boolean} Whether the entire shape is inside the permitted areas.
 */
const isIpawsAreaShapeWithinPermittedArea = (shape, permittedAreas) => {
  try {
    if (!permittedAreas) return false;

    const geometryFeature =
      shape?.type === IPAWS_AREA_TYPES.CIRCLE
        ? createTurfCircle([shape.center.lng, shape.center.lat], Number(shape.radiusKm), {
            steps: 128,
            units: 'kilometers',
          })
        : createTurfPolygon([createClosedTurfRing(getIpawsAreaPolygonCoordinates(shape))]);

    // Checking only the corners is not enough: an edge can cross outside an irregular boundary.
    // Remove the permitted area from the shape and pass only when nothing is left outside.
    return difference(featureCollection([geometryFeature, permittedAreas])) === null;
  } catch {
    // A shape that cannot be checked is safer to reject than to assume it is permitted.
    return false;
  }
};

/**
 * Tells whether a shape has enough valid geometry for the whole-shape check. A polygon that is
 * still being drawn (fewer than three points) is not complete, so the map does not mark it.
 * @param {object} shape The shape to read.
 * @returns {boolean} Whether the shape can go into the whole-shape check.
 */
const isIpawsAreaShapeComplete = (shape) => {
  if (shape?.type === IPAWS_AREA_TYPES.CIRCLE) {
    return isValidWgs84Coordinate(shape.center) && isPositiveDistance(shape.radiusKm);
  }
  if (shape?.type === IPAWS_AREA_TYPES.SQUARE) {
    return isValidWgs84Coordinate(shape.center) && isPositiveDistance(shape.sideLengthKm);
  }
  return createClosedTurfRing(getIpawsAreaPolygonCoordinates(shape)) !== null;
};

// The four edge points of a circle, in the order the warnings list them.
const CIRCLE_EDGE_POINTS = [
  { name: 'north edge', bearing: 0 },
  { name: 'east edge', bearing: 90 },
  { name: 'south edge', bearing: 180 },
  { name: 'west edge', bearing: -90 },
];

/**
 * The four points on a circle's edge due north, east, south, and west of its center.
 * @param {object} shape A circle with a valid center and radius.
 * @returns {{ position: IpawsMapCoordinate, name: string }[]} The four edge points.
 */
const getIpawsCircleEdgePoints = (shape) => {
  const centerPoint = point([shape.center.lng, shape.center.lat]);
  return CIRCLE_EDGE_POINTS.map(({ name, bearing }) => {
    const [longitude, latitude] = destination(centerPoint, Number(shape.radiusKm), bearing, {
      units: 'kilometers',
    }).geometry.coordinates;
    return { position: { lat: latitude, lng: longitude }, name };
  });
};

/**
 * Builds the warning texts for one coordinate point outside the permitted area. The IPAWS map,
 * the IPAWS findings, and the RescAlert map all use this, so each place says the same words.
 * @param {object} options
 * @param {string} options.areaLabel The shape name, such as `Polygon 1`.
 * @param {{ position: IpawsMapCoordinate, number?: number, name?: string }} options.checkPoint
 * A vertex (numbered from 1) or a named circle point, such as `center`.
 * @returns {{ message: string, accessibleName: string }} The finding and the map marker name.
 */
export const describeOutsideCoordinate = ({ areaLabel, checkPoint }) => {
  const { position, number, name } = checkPoint;
  const location = `(${Number(position.lat).toFixed(4)}, ${Number(position.lng).toFixed(4)})`;
  const pointLabel = name ? `${name} point` : `point ${number}`;
  return {
    message: `${areaLabel}, ${pointLabel} ${location} is outside the authorized jurisdiction — move it inside.`,
    accessibleName: name
      ? `The ${name} point of ${areaLabel} is outside the authorized jurisdiction`
      : `Coordinate ${number} of ${areaLabel} is outside the authorized jurisdiction`,
  };
};

/**
 * Every ring of the permitted area (outer rings and holes) as a Turf line, to measure how far a
 * point is from the boundary.
 * @param {{ polygons?: object[][][], rings?: object[][] }} permittedArea From `permittedAreaForDraft`.
 * @returns {object[]} Turf LineString features.
 */
const getPermittedAreaBoundaryLines = (permittedArea) => {
  const polygons =
    Array.isArray(permittedArea?.polygons) && permittedArea.polygons.length
      ? permittedArea.polygons
      : (Array.isArray(permittedArea?.rings) ? permittedArea.rings : []).map((ring) => [ring]);
  return polygons
    .flat()
    .map((ring) => createClosedTurfRing(ring))
    .filter(Boolean)
    .map((ring) => createTurfLineString(ring));
};

/**
 * Prepares the permitted area once, for many shape and point checks. The findings and the map
 * both use this, so the two always agree. When the area is not ready, nothing is marked: the
 * findings already say why the shapes cannot be checked.
 * @param {object|null} permittedArea From `permittedAreaForDraft`.
 * @returns {{ ready: boolean, isShapeInside: Function, isPointInside: Function,
 * distanceToBoundaryKm: Function }} The checks.
 */
export const createIpawsPermittedAreaCheck = (permittedArea) => {
  const permittedAreas = permittedArea?.ready ? combinePermittedArea(permittedArea) : null;
  let boundaryLines = null;
  return {
    ready: permittedAreas !== null,
    isShapeInside: (shape) => isIpawsAreaShapeWithinPermittedArea(shape, permittedAreas),
    isPointInside: (position) => {
      try {
        // A point on the boundary line counts as inside, as it does in the whole-shape check.
        return (
          permittedAreas !== null &&
          isValidWgs84Coordinate(position) &&
          booleanPointInPolygon(point([position.lng, position.lat]), permittedAreas)
        );
      } catch {
        return false;
      }
    },
    distanceToBoundaryKm: (position) => {
      try {
        // The lines are built only when a circle is outside, and then kept for the next point.
        if (!boundaryLines) boundaryLines = getPermittedAreaBoundaryLines(permittedArea);
        const distances = boundaryLines.map((line) =>
          pointToLineDistance(point([position.lng, position.lat]), line, { units: 'kilometers' }),
        );
        return distances.length ? Math.min(...distances) : 0;
      } catch {
        return 0;
      }
    },
  };
};

/**
 * Finds the parts of one shape that are outside the permitted area, with the map marks and the
 * finding lines for them.
 * A polygon or a square gets one mark and one line for each vertex outside.
 * A circle gets one mark and one line when its center is outside. When its drawn edge crosses
 * the boundary, it gets one line that says how far the radius reaches outside. It also gets a
 * mark on each north, east, south, or west edge point outside, or, when none of those is
 * outside, on the first edge point outside.
 * @param {object} shape The shape to check.
 * @param {string} shapeLabel The shape name, such as `Circle 2`.
 * @param {ReturnType<typeof createIpawsPermittedAreaCheck>} areaCheck The prepared check.
 * @returns {{ marks: object[], messages: string[] }} The map marks and the finding lines.
 */
const getIpawsAreaShapeOutsideParts = (shape, shapeLabel, areaCheck) => {
  const marks = [];
  const messages = [];
  const addPoint = (checkPoint, key) => {
    const description = describeOutsideCoordinate({ areaLabel: shapeLabel, checkPoint });
    marks.push({ key, position: checkPoint.position, accessibleName: description.accessibleName });
    messages.push(description.message);
  };

  if (shape?.type === IPAWS_AREA_TYPES.CIRCLE) {
    if (!isIpawsAreaShapeComplete(shape)) return { marks, messages };
    if (!areaCheck.isPointInside(shape.center)) {
      addPoint({ position: shape.center, name: 'center' }, 'center');
    }

    // The edge points are the same 128 points the whole-shape check and the map use.
    const outsideEdge = getIpawsAreaShapeBoundaryCoordinates(shape).filter(
      (position) => !areaCheck.isPointInside(position),
    );
    if (!outsideEdge.length) return { marks, messages };

    const reachKm = Math.max(...outsideEdge.map(areaCheck.distanceToBoundaryKm));
    messages.push(
      `${shapeLabel}: the radius reaches ${reachKm.toFixed(2)} km outside the authorized jurisdiction — shrink it or move the center.`,
    );
    const outsideCardinals = getIpawsCircleEdgePoints(shape).filter(
      (edgePoint) => !areaCheck.isPointInside(edgePoint.position),
    );
    const edgeMarks = outsideCardinals.length
      ? outsideCardinals.map((edgePoint) => ({
          key: edgePoint.name,
          position: edgePoint.position,
          accessibleName: `The ${edgePoint.name} point of ${shapeLabel} is outside the authorized jurisdiction`,
        }))
      : [
          {
            key: 'edge',
            position: outsideEdge[0],
            accessibleName: `The edge of ${shapeLabel} is outside the authorized jurisdiction`,
          },
        ];
    marks.push(...edgeMarks);
    return { marks, messages };
  }

  getIpawsOpenPolygonCoordinates(getIpawsAreaPolygonCoordinates(shape)).forEach(
    (position, index) => {
      if (!isValidWgs84Coordinate(position) || areaCheck.isPointInside(position)) return;
      addPoint({ position, number: index + 1 }, `point-${index + 1}`);
    },
  );
  return { marks, messages };
};

/**
 * Finds the shapes and the coordinate points that are outside the permitted area, for the map.
 * A shape is outside when the whole-shape check fails. This also catches an edge that crosses
 * the boundary between two vertices that are inside.
 * @param {object | object[]} draftOrShapes A current draft or shape list.
 * @param {ReturnType<typeof createIpawsPermittedAreaCheck>} areaCheck The prepared check.
 * @returns {{ outsideShapeIds: string[], outsidePoints: object[] }} The shapes and the marks.
 */
export const getIpawsAreaContainment = (draftOrShapes, areaCheck) => {
  const result = { outsideShapeIds: [], outsidePoints: [] };
  if (!areaCheck?.ready) return result;

  getIpawsAreaShapes(draftOrShapes).forEach((shape, shapeIndex) => {
    const shapeLabel = getIpawsAreaShapeLabel(shape, shapeIndex);
    getIpawsAreaShapeOutsideParts(shape, shapeLabel, areaCheck).marks.forEach((mark) =>
      result.outsidePoints.push({ ...mark, key: `${shape.id ?? shapeIndex}-${mark.key}` }),
    );
    if (isIpawsAreaShapeComplete(shape) && !areaCheck.isShapeInside(shape)) {
      result.outsideShapeIds.push(shape.id);
    }
  });

  return result;
};

/**
 * Determines how many IPAWS/CAP points a shape uses when submitted
 * @param {object} shape The shape to count.
 * @returns {number} The number of entries used by this shape.
 */
export const getIpawsAreaShapePointCount = (shape) => {
  if (shape?.type === IPAWS_AREA_TYPES.CIRCLE) return 1; // Only one entry for a circle because its only one coordinate (the center)

  const openCoordinates = getIpawsOpenPolygonCoordinates(getIpawsAreaPolygonCoordinates(shape));
  return openCoordinates.length ? openCoordinates.length + 1 : 0; // Consumes one entry for each coordinate plus one for the closing point
};

/**
 * Returns the number of CAP point slots that must remain available while drawing a polygon.
 * A valid polygon requires at least three unique coordinates, plus the first coordinate repeated
 * at the end to close the polygon, for a minimum of four CAP points. Once that minimum is met,
 * at least one point slot is still required to allow another vertex to be added.
 * @param {object} shape The polygon currently being drawn.
 * @returns {number} The minimum number of CAP point slots that must remain available.
 */
export const getIpawsPolygonRequiredPointCapacity = (shape) =>
  Math.max(1, 4 - getIpawsAreaShapePointCount(shape));

/**
 * Counts the shapes and final number of point entries used by a draft.
 * @param {object | object[]} draftOrShapes A current draft or shape list.
 * @returns {{ shapeCount: number, pointCount: number }} Current usage totals.
 */
export const getIpawsAreaMetrics = (draftOrShapes = {}) => {
  const shapes = getIpawsAreaShapes(draftOrShapes);

  return {
    shapeCount: shapes.length,
    pointCount: shapes.reduce(
      (pointCount, shape) => pointCount + getIpawsAreaShapePointCount(shape),
      0,
    ),
  };
};

/**
 * Returns one useful center point for each shape.
 * Location search uses one point per shape so a polygon with many corners does not matter more than
 * a circle or square.
 * @param {object | object[]} draftOrShapes A current draft or shape list.
 * @returns {IpawsMapCoordinate[]} One point for each valid shape.
 */
export const getIpawsAreaReferenceCoordinates = (draftOrShapes = {}) => {
  return getIpawsAreaShapes(draftOrShapes)
    .map((shape) => {
      if (shape?.type === IPAWS_AREA_TYPES.CIRCLE || shape?.type === IPAWS_AREA_TYPES.SQUARE) {
        return isValidWgs84Coordinate(shape.center) ? shape.center : null;
      }

      const coordinates = getIpawsOpenPolygonCoordinates(
        getIpawsAreaPolygonCoordinates(shape),
      ).filter(isValidWgs84Coordinate);
      if (!coordinates.length) return null;

      // Hand-drawn polygons do not store a center, so use the average of their corners.
      const totals = coordinates.reduce(
        (result, coordinate) => ({
          lat: result.lat + coordinate.lat,
          lng: result.lng + coordinate.lng,
        }),
        { lat: 0, lng: 0 },
      );

      return {
        lat: totals.lat / coordinates.length,
        lng: totals.lng / coordinates.length,
      };
    })
    .filter(Boolean);
};

/**
 * Finds problems that must be fixed before the map shapes can be submitted.
 * It checks required shapes, size limits, valid points, and whether every complete shape stays
 * inside the authorized area the draft targets.
 * @param {object | object[]} draftOrShapes A current draft or shape list.
 * @param {object} options Information used for the permitted-area check.
 * @param {object|null} options.permittedArea The targeted authorized area from
 * `permittedAreaForDraft` — `{ label, rings, ready, reason }`. Not ready means the shapes cannot
 * be checked yet (no target, the list still loading, no ring for the target) and is reported as
 * a finding; it is never permission to draw anywhere.
 * @returns {string[]} Plain messages that can be shown directly in the form.
 */
export const getIpawsAreaFindings = (draftOrShapes = {}, { permittedArea = null } = {}) => {
  const shapes = getIpawsAreaShapes(draftOrShapes);
  const findings = [];
  const { shapeCount, pointCount } = getIpawsAreaMetrics(shapes);
  const isPermittedAreaReady = Boolean(permittedArea?.ready);
  const areaCheck = createIpawsPermittedAreaCheck(isPermittedAreaReady ? permittedArea : null);
  const permittedLabel = permittedArea?.label || 'the targeted authorized area';
  const notReadyMessage =
    permittedArea?.reason ||
    'The targeted area cannot be checked yet, so the map shapes cannot be validated for IPAWS submission.';
  const isNwemSelected =
    !Array.isArray(draftOrShapes) && draftOrShapes?.deliveryChannels?.includes('NWEM');

  if (!shapeCount) {
    // A resolved county is targeted by its SAME geocode alone; shapes only narrow the area.
    if (hasIpawsSameGeocode(draftOrShapes)) return findings;
    // With nothing drawn there is nothing to check; the SAME-geocode finding names the target.
    findings.push('Add at least one polygon, square, or circle on the map.');
    return findings;
  }

  if (!isPermittedAreaReady) {
    findings.push(notReadyMessage);
  }

  if (shapeCount > IPAWS_AREA_LIMITS.maximumShapeCount) {
    findings.push(`Use no more than ${IPAWS_AREA_LIMITS.maximumShapeCount} total map shapes.`);
  }

  if (pointCount > IPAWS_AREA_LIMITS.maximumPointCount) {
    findings.push(
      `Use no more than ${IPAWS_AREA_LIMITS.maximumPointCount} total CAP points across all map shapes.`,
    );
  }

  shapes.forEach((shape, index) => {
    const shapeLabel = getIpawsAreaShapeLabel(shape, index);
    const shapeFindings = [];

    if (isNwemSelected && shape?.type !== IPAWS_AREA_TYPES.CIRCLE) {
      const polygonCoordinates = getIpawsAreaPolygonCoordinates(shape);
      if (getIpawsAreaShapePointCount(shape) > NWEM_MAXIMUM_POLYGON_POINT_COUNT) {
        shapeFindings.push(
          `${shapeLabel}: use no more than ${NWEM_MAXIMUM_DRAWN_POLYGON_VERTEX_COUNT} drawn vertices for NWEM.`,
        );
      }

      const normalizedCoordinates = polygonCoordinates.map(({ lat, lng }) => ({
        lat: Number(Number(lat).toFixed(3)),
        lng: Number(Number(lng).toFixed(3)),
      }));
      getIpawsPolygonFindings(normalizedCoordinates).forEach((finding) =>
        shapeFindings.push(`${shapeLabel} after NWEM coordinate rounding: ${finding}`),
      );
    }

    if (shape?.type === IPAWS_AREA_TYPES.POLYGON) {
      getIpawsPolygonFindings(shape.coordinates).forEach((finding) =>
        shapeFindings.push(`${shapeLabel}: ${finding}`),
      );
    } else if (shape?.type === IPAWS_AREA_TYPES.SQUARE) {
      if (!isValidWgs84Coordinate(shape.center)) {
        shapeFindings.push(`${shapeLabel}: choose a valid WGS 84 center point.`);
      }
      if (!isPositiveDistance(shape.sideLengthKm)) {
        shapeFindings.push(`${shapeLabel}: side length must be greater than 0 kilometers.`);
      }

      // Runs only for valid squares with no basic shape problems.
      if (!shapeFindings.length) {
        // A square becomes a polygon when submitted, so run the same corner and edge checks now.
        getIpawsPolygonFindings(getIpawsAreaPolygonCoordinates(shape)).forEach((finding) =>
          shapeFindings.push(`${shapeLabel}: ${finding}`),
        );
      }
    } else if (shape?.type === IPAWS_AREA_TYPES.CIRCLE) {
      if (!isValidWgs84Coordinate(shape.center)) {
        shapeFindings.push(`${shapeLabel}: choose a valid WGS 84 center point.`);
      }
      if (!isPositiveDistance(shape.radiusKm)) {
        shapeFindings.push(`${shapeLabel}: radius must be greater than 0 kilometers.`);
      }
    } else {
      shapeFindings.push(`${shapeLabel}: choose a supported map shape type.`);
    }

    if (!shapeFindings.length && isPermittedAreaReady) {
      // Only run the area check after basic shape problems are fixed. This keeps the messages clear
      // and avoids sending broken coordinates into the map library.
      if (!areaCheck.isShapeInside(shape)) {
        shapeFindings.push(`${shapeLabel}: keep the entire shape within ${permittedLabel}.`);
      }
    }

    // One line for each coordinate point outside the area, and one line for a circle edge that
    // crosses the boundary, so the operator knows what to move. A line goes away when that part
    // is inside again.
    if (areaCheck.ready) {
      shapeFindings.push(...getIpawsAreaShapeOutsideParts(shape, shapeLabel, areaCheck).messages);
    }

    findings.push(...shapeFindings);
  });

  return findings;
};
